import * as joda from "@js-joda/core";
import { LocalDate } from "@js-joda/core";
import * as c from "purify-ts";
import h from "trader-hyperscript";
import { GetReturnTypeFromApiSpec } from "trader.ts/router";
import { scheduleForCleanup } from "trader.ts/ui";
import { Field, Form, checkBox, dateBox, selectBox } from "trader.ts/ui.forms";
import { checkAllCasesHandled } from "trader.ts/utils";
import * as style from "../../../styles/style.css.classes";
import {
  downloadCsvFromTable,
  downloadXlsFromTable,
} from "../../client/downloadtable";
import { makeWeeksFromPeriod } from "../../client/weekfield";
import { country } from "../../domain/config";
import { getFromLocalStorage, setLocalStorage } from "../../lib/localstorage";
import { round, roundTo2 } from "../../lib/round";
import { getMonday, getMonthsFromDateRange } from "../../lib/time";
import {
  ddMMMyyyy,
  renderFirstNameLastName,
  wrapInParentheses,
} from "../../render";
import * as routes from "../../routes";
import { translateMonth } from "../../translations_specific";
import {
  renderReportResult,
  runQueryOnFormChange,
} from "../finance_invoices/client";

export function render(opts: {
  earliest_enrollment_date: joda.LocalDate;
  country: country;
}): HTMLElement | HTMLElement[] {
  const today = LocalDate.now();

  const months = getMonthsFromDateRange(
    opts.earliest_enrollment_date,
    today.plusMonths(6),
  ).reverse();

  const weeks = makeWeeksFromPeriod({
    firstday: getMonday(opts.earliest_enrollment_date),
    lastday: today.plusMonths(6),
  }).reverse();

  const countStudentLegalAbsencesLocalStorage = {
    key: "classy-lesson-statistics-count-student-legal-absences",
    codec: c.boolean,
  };
  const countStudentLegalAbsencesInitial =
    getFromLocalStorage(countStudentLegalAbsencesLocalStorage) ?? false;

  const form = new Form({})
    .addField("dates_calc", [], () => {
      const values = ["month" as const, "week" as const, "custom" as const];
      return selectBox({
        values,
        initial: values[0],
        saveAndLoadInitialValToLocalStorage:
          "classy-lesson-statistics-dates-calc",
        show: (v) =>
          v === "month"
            ? "Per maand"
            : v === "week"
            ? "Per week"
            : v === "custom"
            ? "Zelf data selecteren"
            : checkAllCasesHandled(v),
      });
    })
    .addField(
      "dates",
      ["dates_calc"],
      async ({
        dates_calc,
      }): Promise<
        Field<{ dates: { firstday: LocalDate; lastday: LocalDate } }>
      > => {
        if (dates_calc === "custom") {
          return (
            new Form({})
              // Nested field is just to conform with structure of other branch
              .addField("dates", [], async () =>
                new Form({})
                  .addField("firstday", [], () => dateBox({}))
                  .addField("lastday", [], () => dateBox({}))
                  .build({
                    render: (fields) => (
                      <div className={style.equal_columns_non_responsive}>
                        {fields.firstday()}
                        {fields.lastday()}
                      </div>
                    ),
                  }),
              )
              .build()
          );
        } else {
          const values =
            dates_calc === "month"
              ? months.map(([year, month]) => {
                  const firstday = joda.LocalDate.of(year, month, 1);
                  const lastday = firstday.plusMonths(1).minusDays(1);
                  return {
                    dates: { firstday, lastday },
                    label: translateMonth(month) + " " + year.toString(),
                  };
                })
              : dates_calc === "week"
              ? weeks.map((week) => ({
                  dates: { firstday: week.firstday, lastday: week.lastday },
                  label:
                    ddMMMyyyy(week.firstday) + " - " + ddMMMyyyy(week.lastday),
                }))
              : checkAllCasesHandled(dates_calc);
          const initial = values.find(
            (v) =>
              (v.dates.firstday.isEqual(today) ||
                v.dates.firstday.isBefore(today)) &&
              (v.dates.lastday.isEqual(today) ||
                v.dates.lastday.isAfter(today)),
          );
          return selectBox({
            values,
            initial,
            show: (v) =>
              v.label +
              (v === initial
                ? " " +
                  (dates_calc === "month"
                    ? wrapInParentheses("Huidige maand")
                    : dates_calc === "week"
                    ? wrapInParentheses("Huidige week")
                    : checkAllCasesHandled(dates_calc))
                : ""),
          });
        }
      },
    )
    .addField("split_on_formulas", [], () =>
      checkBox({
        initialVal: opts.country === "Netherlands" ? true : false,
        label: "Splits op formules",
        class: style.input_like + style.checkbox,
      }),
    )
    .addField("count_student_legal_absences", [], () =>
      checkBox({
        initialVal: countStudentLegalAbsencesInitial,
        label: "Tel privélessen waar student afwezig was en waar vervangles voor is ingepland mee",
        class: style.input_like + style.checkbox,
      }),
    )
    .build({
      render: (fields) => (
        <section
          className={style.cancel_main_margins}
          style="margin-top: -32px; align-items: flex-start;"
        >
          <article>
            <label for="">
              <div className="label-text">{"Data"}</div>
              <div className={style.equal_columns}>
                {fields.dates_calc()}
                {fields.dates()}
              </div>
            </label>
          </article>

          <article>
            <label for="">
              <div className="label-text">{"Opties"}</div>
              <div className={style.equal_columns}>
                {fields.split_on_formulas()}
                {fields.count_student_legal_absences()}
              </div>
            </label>
          </article>
        </section>
      ),
    });

  scheduleForCleanup(
    form.s.observe((val) => {
      if (val.tag === "parsed") {
        setLocalStorage(
          countStudentLegalAbsencesLocalStorage,
          val.parsed.count_student_legal_absences,
        );
      }
    }),
  );

  const id = "classy-table-id";

  const res = runQueryOnFormChange(
    form.s,
    routes.getLessonStatisticSpec,
    (t) => t.dates.dates,
  );

  return (
    <div>
      <section
        className={style.cancel_main_margins}
        style="background-color: var(--grey-200); padding-bottom: 32px;"
      >
        {"Deze statistiek toont je hoeveel lessen elke docent gaf in een bepaalde maand, of zal geven. Je kan dit oa. gebruiken bij de uitbetaling van docenten, of om de evolutie van de werklast te volgen."}
      </section>

      {form.render()}

      <section>
        <div style="text-align: right;">
          <div style="font-size: 0.85em;">
            <a
              style="margin-right: 8px;"
              onclick={() =>
                downloadXlsFromTable("OverzichtLessen", id)
              }
            >
              {"Download als xls"}
            </a>
            <a
              onclick={() =>
                downloadCsvFromTable("OverzichtLessen", id)
              }
            >
              {"Download als csv"}
            </a>
          </div>
        </div>
        {renderReportResult(res, (rows, query) => {
          const thStyle = "vertical-align: bottom; ";
          const header = (
            <tr>
              <th style={thStyle}>{"Docent"}</th>
              <th style={thStyle}>
                {query.split_on_formulas ? "Formule" : ""}
              </th>
              <th style={thStyle}>{"Aantal"}</th>
              <th style={thStyle}>{"Uren"}</th>
            </tr>
          );

          const mode =
            opts.country === "Belgium"
              ? query.split_on_formulas
                ? ("GroupedByFormula" as const)
                : ("NoGrouping" as const)
              : opts.country === "Netherlands"
              ? query.split_on_formulas
                ? ("GroupedByFormulaAndVATType" as const)
                : ("NoGrouping" as const)
              : checkAllCasesHandled(opts.country);

          const totalRows = rows
            .orderBy((row) => renderFirstNameLastName(row))
            .flatMap((row, i) => {
              return calculateLessonTotals(row, mode)
                .orderBy((t) => t.extraDescription || "")
                .map((t) => ({
                  ...t,
                  grey: i % 2 === 0,
                }));
            });
          const renderedRows = totalRows.map((totalRow) => {
            return (
              <tr
                style={
                  totalRow.grey ? "background-color: var(--grey-100);" : ""
                }
              >
                <td>{totalRow.teacherName}</td>
                <td>{totalRow.extraDescription || ""}</td>
                <td>{totalRow.numberOfLessons}</td>
                <td>
                  {round(
                    (totalRow.totalDuration -
                      (query.count_student_legal_absences
                        ? 0
                        : totalRow.totalDurationLegalAbsenceConfirmed)) /
                      60,
                    2,
                  )}
                </td>
              </tr>
            );
          });

          const footer = (
            <tr style="font-weight: bold; background-color: var(--grey-100)">
              <td>{"Totaal"}</td>
              <td></td>
              <td>{roundTo2(sum(totalRows.map((r) => r.numberOfLessons)))}</td>
              <td>
                {roundTo2(
                  sum(
                    totalRows.map(
                      (r) =>
                        r.totalDuration -
                        (query.count_student_legal_absences
                          ? 0
                          : r.totalDurationLegalAbsenceConfirmed),
                    ),
                  ) / 60,
                )}
              </td>
            </tr>
          );

          return (
            <table id={id}>
              {header}
              {renderedRows}
              {footer}
            </table>
          );
        })}
      </section>
    </div>
  );
}

function calculateLessonTotals(
  teacher: GetReturnTypeFromApiSpec<
    typeof routes.getLessonStatisticSpec
  >[number],
  mode: "NoGrouping" | "GroupedByFormula" | "GroupedByFormulaAndVATType",
): {
  teacherName: string;
  extraDescription: string | null;
  numberOfLessons: number;
  totalDuration: number; // minutes
  totalDurationLegalAbsenceConfirmed: number; // minutes
}[] {
  if (mode === "NoGrouping") {
    return [
      {
        teacherName: renderFirstNameLastName(teacher),
        extraDescription: null,
        numberOfLessons: sum(
          teacher.formulas.flatMap((f) =>
            f.vattypes.map((t) => t.numberOfLessons),
          ),
        ),
        totalDuration: sum(
          teacher.formulas.flatMap((f) =>
            f.vattypes.map((t) => t.totalDuration),
          ),
        ),
        totalDurationLegalAbsenceConfirmed: sum(
          teacher.formulas.flatMap((f) =>
            f.vattypes.map((t) => t.totalDurationLegalAbsence),
          ),
        ),
      },
    ];
  }
  if (mode === "GroupedByFormula") {
    return teacher.formulas.map((formula) => {
      return {
        teacherName: renderFirstNameLastName(teacher),
        extraDescription: formula.description,
        numberOfLessons: sum(formula.vattypes.map((t) => t.numberOfLessons)),
        totalDuration: sum(formula.vattypes.map((t) => t.totalDuration)),
        totalDurationLegalAbsenceConfirmed: sum(
          formula.vattypes.map((t) => t.totalDurationLegalAbsence),
        ),
      };
    });
  }
  if (mode === "GroupedByFormulaAndVATType") {
    return teacher.formulas.flatMap((formula) => {
      return formula.vattypes.map((vattype) => {
        const vattypeDescr =
          vattype.vattype === "GroupVAT"
            ? "(" + "groepsles" + ")"
            : vattype.vattype === "NormalVAT"
            ? "Met BTW"
            : vattype.vattype === "ReducedToZeroVAT"
            ? "Geen BTW"
            : checkAllCasesHandled(vattype.vattype);
        return {
          teacherName: renderFirstNameLastName(teacher),
          extraDescription: formula.description + " - " + vattypeDescr,
          numberOfLessons: vattype.numberOfLessons,
          totalDuration: vattype.totalDuration,
          totalDurationLegalAbsenceConfirmed: vattype.totalDurationLegalAbsence,
        };
      });
    });
  }
  return checkAllCasesHandled(mode);
}

function sum(n: number[]): number {
  return n.reduce((acc, s) => acc + s, 0);
}
